'use client';

/**
 * "Enjoying CheerVue?" — the dashboard card inviting gym owners and admins to
 * rate CheerVue.
 *
 * This component is deliberately DUMB. Every targeting rule (feature flag, org
 * allowlist, owner/admin role, tenure, cool-downs, demo gym, platform admins,
 * support sessions) lives behind GET /api/feedback-prompt/eligibility. The card
 * paints exactly what that authenticated route returns and knows none of the
 * rules — so nothing about who is asked, or why, leaks into the browser.
 *
 * It FAILS QUIET. An error, a 401, a malformed body, or a missing session all
 * render nothing at all. A request for a favor is never worth an error state in
 * a gym owner's face.
 *
 * Visual intent: this is an invitation, NOT an alert. The other dashboard
 * callouts (families to connect, missed tuition) are white cards with a brand
 * bar down the left edge — that treatment means "something needs you". This one
 * is deliberately different: tinted, illustrated, no left bar, so it reads as
 * friendly rather than as one more task. It must never out-shout the callouts
 * about money, which is why it sits BELOW them in the dashboard order.
 *
 * Layout: INLINE in the dashboard flow, never fixed/floating, so it can't
 * overlap the floating chat bubble (bottom-right) on a phone.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/utils/supabase/client';
import { createLogger } from '@/utils/logger/devLogger';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import Button from '@/components/ui/button/Button';

const logger = createLogger('FeedbackPromptCard');

/** Context tag carried on the openChatAssistant event. ChatAssistant branches on
 *  this to start the rating in its OWN conversation instead of injecting it into
 *  whatever thread the user happened to have open. */
export const FEEDBACK_RATING_CHAT_CONTEXT = 'feedback_rating';

/** The user-voice message that starts the rating conversation. Phrased the way a
 *  gym owner would say it so it lands on the assistant's rate_cheervue knowledge. */
export const FEEDBACK_RATING_ASSISTANT_MESSAGE = "I'd like to rate CheerVue.";

type FeedbackPromptEvent = 'opened' | 'dismissed';

/**
 * Bearer header when a session is available. Staff dashboard components send the
 * token explicitly rather than relying on cookies alone (same as the other
 * dashboard callouts); no session simply means no header and a quiet 401.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Focus a container that isn't natively focusable (standard skip-link pattern). */
function focusContainer(element: HTMLElement | null | undefined): boolean {
  if (!element || element === document.body || !element.isConnected) return false;
  if (!element.hasAttribute('tabindex')) {
    element.setAttribute('tabindex', '-1');
  }
  element.focus();
  return document.activeElement === element;
}

/**
 * Keyboard users must not be dumped on <body> when the card unmounts.
 *
 * In order: whatever had focus before the card appeared, then the main dashboard
 * landmark, then the container the card was sitting in. That last step is not a
 * theoretical fallback — the gym-admin layout ships no <main> element, so the
 * container is what actually catches focus in production, and it also keeps the
 * next Tab press continuing from where the card was.
 */
function moveFocusToFallback(previous: HTMLElement | null, container: HTMLElement | null): void {
  if (
    previous &&
    previous !== document.body &&
    previous.isConnected &&
    typeof previous.focus === 'function'
  ) {
    previous.focus();
    if (document.activeElement === previous) return;
  }

  const main = document.querySelector('main');
  if (focusContainer(main instanceof HTMLElement ? main : null)) return;

  focusContainer(container);
}

/** A single 5-point star inside a 9x9 box, positioned by translate. */
const STAR_PATH =
  'M4.5 0 L5.83 2.87 L9 3.32 L6.69 5.56 L7.23 8.73 L4.5 7.23 L1.77 8.73 L2.31 5.56 L0 3.32 L3.17 2.87 Z';

/**
 * Speech bubble holding five stars, with a little confetti.
 *
 * Everything is drawn in `currentColor` at varying opacity rather than fixed
 * fills, so one text-colour class themes the whole illustration and it stays
 * correct in dark mode without a second set of values. Five stars, not three —
 * it should read as the 1-to-5 scale the assistant is about to ask for.
 */
function RatingIllustration() {
  return (
    <svg
      viewBox="0 0 88 88"
      className="h-20 w-20 sm:h-24 sm:w-24"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="44" cy="44" r="42" fill="currentColor" opacity="0.1" />

      {/* confetti */}
      <circle cx="12" cy="18" r="2.5" fill="currentColor" opacity="0.5" />
      <circle cx="77" cy="29" r="2" fill="currentColor" opacity="0.4" />
      <circle cx="71" cy="69" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="19" cy="70" r="2" fill="currentColor" opacity="0.45" />

      {/* speech bubble with tail */}
      <path
        d="M24,24 H64 a10,10 0 0 1 10,10 V46 a10,10 0 0 1 -10,10 H38 l-10,8 v-8 h-4 a10,10 0 0 1 -10,-10 V34 a10,10 0 0 1 10,-10 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* the 1-5 scale */}
      {[18.5, 29, 39.5, 50, 60.5].map(x => (
        <path key={x} d={STAR_PATH} transform={`translate(${x} 35.5)`} fill="currentColor" />
      ))}
    </svg>
  );
}

interface FeedbackPromptCardProps {
  /** Layout-only overrides for the wrapper (the card owns its own look). */
  className?: string;
}

export default function FeedbackPromptCard({ className = '' }: FeedbackPromptCardProps) {
  const [visible, setVisible] = useState(false);
  /** One 'opened' row per dashboard load. */
  const openedRecordedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  /** Captured while the card is still mounted — it has no parent once it unmounts. */
  const fallbackContainerRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(false);

  // One eligibility check per dashboard load. No polling: the answer can only
  // change because of something this user just did, and both of those paths
  // (open / dismiss) already hide the card locally.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch('/api/feedback-prompt/eligibility', {
          cache: 'no-store',
          headers: await authHeaders(),
        });
        if (cancelled || !response.ok) return;

        const data = await response.json();
        // Strictly `=== true`: a truthy check would paint the card on any
        // unexpected body shape the route might grow later.
        if (cancelled || data?.eligible !== true) return;

        previouslyFocusedRef.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setVisible(true);
      } catch (error) {
        // Fail quiet — no card is always an acceptable outcome.
        logger.debug('Feedback prompt eligibility check failed; card stays hidden', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Focus lands somewhere sensible AFTER the card has left the DOM.
  useEffect(() => {
    if (visible || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    moveFocusToFallback(previouslyFocusedRef.current, fallbackContainerRef.current);
  }, [visible]);

  const recordEvent = useCallback(async (event: FeedbackPromptEvent) => {
    try {
      await fetch('/api/feedback-prompt/event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({ event }),
      });
    } catch (error) {
      // The fatigue rules lose one data point; the user's experience is unchanged.
      logger.warn('Feedback prompt event not recorded', { event, error });
    }
  }, []);

  const handleOpen = useCallback(() => {
    if (!openedRecordedRef.current) {
      openedRecordedRef.current = true;
      void recordEvent('opened');
    }

    window.dispatchEvent(
      new CustomEvent('openChatAssistant', {
        detail: {
          context: FEEDBACK_RATING_CHAT_CONTEXT,
          autoMessage: FEEDBACK_RATING_ASSISTANT_MESSAGE,
        },
      })
    );

    // The invitation has been accepted — the rating now lives in the chat
    // window, so the card has nothing left to offer. Leaving it up invites a
    // second click, and a second click restarts the conversation, wiping the
    // question the user is part-way through answering. Focus is restored the
    // same way as on dismiss: the chat window does not take focus itself, so
    // without this a keyboard user is dropped onto <body>.
    fallbackContainerRef.current = wrapperRef.current?.parentElement ?? null;
    restoreFocusRef.current = true;
    setVisible(false);
  }, [recordEvent]);

  const handleDismiss = useCallback(() => {
    fallbackContainerRef.current = wrapperRef.current?.parentElement ?? null;
    restoreFocusRef.current = true;
    setVisible(false);
    void recordEvent('dismissed');
  }, [recordEvent]);

  if (!visible) return null;

  return (
    <div
      ref={wrapperRef}
      className={`relative mb-6 overflow-hidden rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-25 via-white to-white p-5 sm:p-6 dark:border-brand-500/25 dark:from-brand-500/10 dark:via-transparent dark:to-transparent ${className}`.trim()}
      data-testid="feedback-prompt-card"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="No thanks"
        data-testid="feedback-prompt-card-dismiss"
        className="absolute right-3 top-3 rounded-full p-1 text-gray-400 transition-colors hover:bg-white/70 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-white/10 dark:hover:text-gray-200"
      >
        <GoogleMaterialIcon name="close" size="sm" />
      </button>

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
        <span className="shrink-0 text-brand-500 dark:text-brand-400">
          <RatingIllustration />
        </span>

        <div className="min-w-0 flex-1 pr-6 sm:pr-0">
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg dark:text-white">
            Enjoying CheerVue?
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Tell us how it&rsquo;s going — it takes about 10 seconds, and it goes straight to the
            people who build CheerVue.
          </p>
        </div>

        <Button
          size="sm"
          variant="primary"
          onClick={handleOpen}
          data-testid="feedback-prompt-card-open"
          className="w-full shrink-0 sm:w-auto"
        >
          Rate CheerVue
        </Button>
      </div>
    </div>
  );
}
